"use client";

import React from "react";
import { MapPin, Phone, Mail, Clock, Loader, Send } from "lucide-react";
import { FormProvider, useWvcForm } from "@/integrations/forms/FormProvider";
import { Button } from "@/components/ui/button";
import { Form, FormField, FormItem, FormLabel, FormControl, FormMessage } from "@/components/ui/form";
import { Textarea } from "@/components/ui/textarea";
import { Link } from "@/components/common/Link";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
const Input = React.forwardRef<HTMLInputElement, React.InputHTMLAttributes<HTMLInputElement>>(({
  className,
  ...props
}, ref) => {
  return <input className={`
          flex h-12 w-full rounded-none border-2 border-border bg-secondary/50 px-3 py-2 text-base shadow-none 
          transition-[color,box-shadow,border-color] outline-none 
          placeholder:text-muted-foreground 
          focus-visible:border-primary focus-visible:ring-0
          disabled:cursor-not-allowed disabled:opacity-50 
          md:text-sm
          ${className}
        `} ref={ref} {...props} />;
});
Input.displayName = "Input";
export default function BeginTheConversation() {
  const {
    ref,
    isVisible
  } = useRevealOnScroll({
    threshold: 0.1
  });
  return <section data-section-id="25943" id="dispatch-a-truck-today" ref={ref} className={`relative w-full bg-gradient-to-br from-background to-secondary/30 py-24 transition-opacity duration-700 ${isVisible ? "opacity-100" : "opacity-0"}`}>
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 gap-12 lg:grid-cols-2 lg:gap-0">
          {}
          <div className="flex flex-col gap-10 lg:pr-12 xl:pr-20">
            <div className="space-y-6">
              <h2 className="text-sm font-default font-extrabold uppercase tracking-widest text-primary">
                Immediate Dispatch
              </h2>
              <div className="space-y-2">
                <Link to="tel:+14695904364" className="block text-5xl font-default font-extrabold tracking-tighter text-foreground hover:text-primary transition-colors sm:text-7xl">
                  +1-469-590-4364
                </Link>
                <p className="text-xl font-default font-medium text-muted-foreground max-w-md">
                  Last-mile delivery and heavy-haul logistics across DFW and the Southeast. Ready to roll when you are.
                </p>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-6 pt-8">
                <div className="flex items-start gap-4">
                  <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-none bg-primary text-primary-foreground">
                    <MapPin className="h-6 w-6" />
                  </div>
                  <div>
                    <h3 className="font-default font-bold uppercase text-xs tracking-wider text-foreground">Hub Location</h3>
                    <p className="mt-1 text-sm font-mono text-muted-foreground">
                      Dallas / Ft Worth Metroplex<br />
                      Texas, USA
                    </p>
                  </div>
                </div>

                <div className="flex items-start gap-4">
                  <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-none bg-primary text-primary-foreground">
                    <Mail className="h-6 w-6" />
                  </div>
                  <div>
                    <h3 className="font-default font-bold uppercase text-xs tracking-wider text-foreground">Direct Email</h3>
                    <p className="mt-1 text-sm font-mono text-muted-foreground">
                      <Link to="mailto:dispatch@transitlink.com" className="hover:text-primary transition-colors">
                        dispatch@transitlink.com
                      </Link>
                    </p>
                  </div>
                </div>

                <div className="flex items-start gap-4">
                  <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-none bg-primary text-primary-foreground">
                    <Clock className="h-6 w-6" />
                  </div>
                  <div>
                    <h3 className="font-default font-bold uppercase text-xs tracking-wider text-foreground">Operations</h3>
                    <p className="mt-1 text-sm font-mono text-muted-foreground">
                      24/7 Logistics Support
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {}
            <div className="relative h-[250px] w-full overflow-hidden rounded-none border-2 border-border shadow-[4px_4px_0px_0px_rgba(0,0,0,0.3)]">
              <iframe src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d429158.3888371306!2d-97.10014041797371!3d32.82058652496266!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x864c1e7ae5403061%3A0x673456801932338c!2sDallas-Fort%20Worth%20Metropolitan%20Area!5e0!3m2!1sen!2sus!4v1715800000000!5m2!1sen!2sus" width="100%" height="100%" style={{
              border: 0,
              filter: "grayscale(100%) invert(90%) contrast(110%)"
            }} allowFullScreen={false} loading="lazy" referrerPolicy="no-referrer-when-downgrade" title="TransitLink Service Area" className="absolute inset-0" />
            </div>
          </div>

          {}
          <div className="relative bg-card p-8 shadow-[8px_8px_0px_0px_rgba(0,0,0,0.5)] border-2 border-border sm:p-10 lg:p-12 rounded-none">
            <div className="mb-8">
              <h2 className="text-3xl font-default font-extrabold tracking-tighter text-card-foreground">
                Request a Quote
              </h2>
              <p className="mt-2 text-muted-foreground font-mono text-sm">
                Provide your load details below. Our dispatchers will calculate the most efficient route and pricing for your bulk materials.
              </p>
            </div>

            <FormProvider formKey="dispatch_truck_form_home" formId="dispatch_truck_form_home" sectionName="contact-section" defaultValues={{
            full_name: "",
            email: "",
            phone: "",
            company: "",
            message: ""
          }} validationRules={{
            full_name: {
              required: true
            },
            email: {
              required: true,
              pattern: "email"
            },
            phone: {
              pattern: "phone"
            },
            message: {
              required: true
            }
          }} submitText="Send Request" successMessage="Logistics request received. A dispatcher will contact you within 15 minutes.">
              <ContactForm />
            </FormProvider>
          </div>
        </div>
      </div>
    </section>;
}
function ContactForm() {
  const {
    isSubmitting,
    isSubmitted,
    submitError,
    successMessage,
    handleSubmit,
    resetForm,
    control
  } = useWvcForm();
  if (isSubmitted && !submitError) {
    return <div className="flex flex-col items-center justify-center space-y-4 rounded-none bg-primary/10 p-8 text-center border-2 border-primary">
        <div className="rounded-none bg-primary p-3 text-primary-foreground">
          <Send className="h-6 w-6" />
        </div>
        <h3 className="text-xl font-default font-extrabold text-foreground uppercase tracking-tight">Transmission Received</h3>
        <p className="text-muted-foreground font-mono text-sm">{successMessage}</p>
        <Button onClick={resetForm} variant="outline" className="mt-4 rounded-none border-2 border-primary text-primary hover:bg-primary hover:text-primary-foreground font-default font-bold">
          Submit New Request
        </Button>
      </div>;
  }
  return <Form onSubmit={handleSubmit} className="space-y-5">
      <FormField control={control} name="full_name" render={({
      field
    }) => <FormItem>
            <FormLabel className="font-default font-bold uppercase text-xs tracking-wider">Full Name</FormLabel>
            <FormControl>
              <Input placeholder="John Smith" {...field} />
            </FormControl>
            <FormMessage />
          </FormItem>} />

      <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
        <FormField control={control} name="email" render={({
        field
      }) => <FormItem>
              <FormLabel className="font-default font-bold uppercase text-xs tracking-wider">Email Address</FormLabel>
              <FormControl>
                <Input type="email" placeholder="john@company.com" {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>} />

        <FormField control={control} name="phone" render={({
        field
      }) => <FormItem>
              <FormLabel className="font-default font-bold uppercase text-xs tracking-wider">Phone <span className="text-muted-foreground font-mono font-normal text-[10px] ml-1">(Optional)</span></FormLabel>
              <FormControl>
                <Input type="tel" placeholder="(469) 555-0123" {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>} />
      </div>

      <FormField control={control} name="company" render={({
      field
    }) => <FormItem>
            <FormLabel className="font-default font-bold uppercase text-xs tracking-wider">Company / Job Site <span className="text-muted-foreground font-mono font-normal text-[10px] ml-1">(Optional)</span></FormLabel>
            <FormControl>
              <Input placeholder="Lumber Yard / Stone Supplier" {...field} />
            </FormControl>
            <FormMessage />
          </FormItem>} />

      <FormField control={control} name="message" render={({
      field
    }) => <FormItem>
            <FormLabel className="font-default font-bold uppercase text-xs tracking-wider">Delivery Details</FormLabel>
            <FormControl>
              <Textarea placeholder="Describe material type, quantity, and destination coordinates..." className="min-h-[140px] resize-y rounded-none border-2 border-border bg-secondary/50 focus-visible:border-primary focus-visible:ring-0" {...field} />
            </FormControl>
            <FormMessage />
          </FormItem>} />

      {submitError && <div className="rounded-none bg-destructive/10 p-4 text-xs font-mono text-destructive border-2 border-destructive">
          Error: {submitError}
        </div>}

      <Button type="submit" disabled={isSubmitting} className="w-full sm:w-auto rounded-none bg-primary text-primary-foreground font-default font-extrabold uppercase tracking-widest hover:bg-foreground hover:text-background transition-all shadow-[4px_4px_0px_0px_rgba(0,0,0,0.2)]" size="lg">
        {isSubmitting ? <>
            <Loader className="mr-2 h-4 w-4 animate-spin" />
            Processing...
          </> : "Send Request"}
      </Button>
    </Form>;
}